const entryPattern = /^\/assets\/[A-Za-z0-9_.-]+\.js$/;

export function deployedEntry(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return;
  const metadata = value as { schema?: unknown; entry?: unknown };
  if (metadata.schema === 1 && typeof metadata.entry === 'string' && entryPattern.test(metadata.entry)) return metadata.entry;
}

export function runningEntry(document: Document): string | undefined {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  if (!script) return;
  const path = new URL(script.src, document.baseURI).pathname;
  return entryPattern.test(path) ? path : undefined;
}

export function watchDeploymentUpdates({ entry, onUpdate, fetcher = fetch, target = window, page = document }: {
  entry: string;
  onUpdate: (entry: string) => void;
  fetcher?: typeof fetch;
  target?: Window;
  page?: Document;
}): () => void {
  let stopped = false;
  let pending = false;
  let checkedAt = -Infinity;
  let announced = entry;
  let controller: AbortController | undefined;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  async function check(force = false) {
    if (stopped || pending || page.visibilityState === 'hidden' || (!force && Date.now() - checkedAt < 30_000)) return;
    pending = true;
    checkedAt = Date.now();
    controller = new AbortController();
    timeout = setTimeout(() => controller?.abort(), 5_000);
    try {
      const response = await fetcher('/build-meta.json', { cache: 'no-store', credentials: 'omit', redirect: 'error', signal: controller.signal });
      if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) return;
      const next = deployedEntry(await response.json());
      if (!stopped && next && next !== entry && next !== announced) { announced = next; onUpdate(next); }
    } catch { /* Offline, old servers and interrupted checks must not interrupt a task. */ }
    finally { clearTimeout(timeout); pending = false; }
  }
  const onFocus = () => void check();
  const onPreloadError = () => void check(true);
  target.addEventListener('focus', onFocus);
  target.addEventListener('vite:preloadError', onPreloadError);
  page.addEventListener('visibilitychange', onFocus);
  const interval = setInterval(onFocus, 5 * 60_000);
  void check();
  return () => {
    stopped = true;
    clearInterval(interval);
    clearTimeout(timeout);
    controller?.abort();
    target.removeEventListener('focus', onFocus);
    target.removeEventListener('vite:preloadError', onPreloadError);
    page.removeEventListener('visibilitychange', onFocus);
  };
}
